import { useState, useEffect } from 'react';
import API from '../lib/api';

/**
 * ConvertCurrencyModal — conversion COP <-> USD (dual balance, fee 0.5% explicito).
 * Extraido de Dashboard.jsx para reusarlo en Business (27-ago). El Dashboard de
 * personas conserva su copia local por ahora (deduplicar despues con calma).
 * Nota Business: el vault de la empresa ES el del dueno, asi que el endpoint
 * /transaction/convert (con su SMS) opera la plata correcta cuando entra el dueno.
 */
export default function ConvertCurrencyModal({ balanceCop, balanceUsdc, onClose, onSuccess, initialDirection, initialAmount }) {
  const [direction, setDirection] = useState(initialDirection || 'COP_TO_USD');
  const [amount, setAmount] = useState(initialAmount != null ? String(initialAmount) : '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [rate, setRate] = useState(null); // sin tasa no hay preview (nada de 4140 quemado)
  const [refreshIn, setRefreshIn] = useState(60);

  // Misma UX del Transferir de produccion: cotizacion viva con contador de refresco
  const fetchRate = () => {
    API.get('/transaction/get_exchange_rate')
      .then((res) => { if (res.data?.rate) setRate(res.data.rate); })
      .catch(() => {});
  };
  useEffect(() => { fetchRate(); }, []);
  useEffect(() => {
    const t = setInterval(() => {
      setRefreshIn((s) => {
        if (s <= 1) { fetchRate(); return 60; }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(t);
  }, []);

  const numericAmount = parseFloat(String(amount).replace(/[^0-9.]/g, '')) || 0;
  const fromMax = direction === 'COP_TO_USD' ? balanceCop : balanceUsdc;
  const exceedsBalance = numericAmount > fromMax;
  // Espejo EXACTO del backend (convertCurrency): fee 0.5% sobre el monto de
  // origen, y se convierte el NETO. La comision va explicita, nunca escondida
  // en la tasa (modelo Siigo / Art. 420 ET, igual que el Transferir de prod).
  const feePct = parseFloat(import.meta.env.VITE_DEVELOPER_FEE) || 0.5;
  const feeAmount = numericAmount * (feePct / 100);
  const netAmount = numericAmount - feeAmount;
  const previewConverted = rate ? (direction === 'COP_TO_USD' ? netAmount / rate : netAmount * rate) : 0;

  const handleConvert = async () => {
    if (!numericAmount || exceedsBalance) return;
    setLoading(true);
    setError('');
    try {
      const res = await API.post('/transaction/convert', { direction, amount: numericAmount });
      if (res.data?.success) {
        onSuccess({
          balance_cop: res.data.data.balance_cop,
          balance_usdc: res.data.data.balance_usdc,
        });
      } else {
        setError(res.data?.message || 'No se pudo realizar la conversion');
      }
    } catch (err) {
      setError(err?.response?.data?.message || 'Error en el servidor');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-[20px] w-full max-w-md p-6 space-y-4">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-bold text-[#0A2540]">Convertir</h3>
          <button onClick={onClose} className="text-2xl text-[#9CA3AF]">&times;</button>
        </div>

        <div className="flex bg-[#F3F4F6] rounded-[12px] p-1">
          <button
            onClick={() => setDirection('COP_TO_USD')}
            className={`flex-1 py-2 rounded-[10px] text-sm font-semibold ${
              direction === 'COP_TO_USD' ? 'bg-white text-[#0A2540] shadow-sm' : 'text-[#6B7280]'
            }`}
          >
            Pesos a Dolares
          </button>
          <button
            onClick={() => setDirection('USD_TO_COP')}
            className={`flex-1 py-2 rounded-[10px] text-sm font-semibold ${
              direction === 'USD_TO_COP' ? 'bg-white text-[#0A2540] shadow-sm' : 'text-[#6B7280]'
            }`}
          >
            Dolares a Pesos
          </button>
        </div>

        <div>
          <p className="text-xs text-[#6B7280] font-semibold mb-1">
            Cuanto quieres convertir ({direction === 'COP_TO_USD' ? 'COP' : 'USD'})
          </p>
          <input
            type="text"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder={direction === 'COP_TO_USD' ? '100.000' : '50.00'}
            className="w-full p-3 border-2 border-[#C9A961] rounded-[12px] text-2xl font-bold text-[#0A2540] text-center"
          />
          <p className="text-[11px] text-[#9CA3AF] text-center mt-1">
            Disponible: {direction === 'COP_TO_USD'
              ? `$ ${(fromMax || 0).toLocaleString('es-CO')} COP`
              : `$ ${(fromMax || 0).toFixed(2)} USD`}
          </p>
        </div>

        {rate && (
          <div className="bg-[#F0F4FF] rounded-[12px] px-3 py-2 flex justify-between items-center">
            <span className="text-xs font-semibold text-[#0A2540]">Cotizacion actual</span>
            <span className="text-xs font-bold text-[#0A2540]">1 USD = $ {rate.toLocaleString('es-CO')} COP</span>
          </div>
        )}
        {rate && (
          <p className="text-[11px] text-[#6B7280] text-center -mt-2">
            La cotizacion se actualizara en {refreshIn} segundos
          </p>
        )}

        {numericAmount > 0 && rate && (() => {
          const esCopOrigen = direction === 'COP_TO_USD';
          const fmtOrigen = (v) => esCopOrigen
            ? `$ ${Math.round(v).toLocaleString('es-CO')} COP`
            : `$ ${v.toFixed(2)} USD`;
          const fmtDestino = (v) => esCopOrigen
            ? `$ ${v.toFixed(2)} USD`
            : `$ ${Math.round(v).toLocaleString('es-CO')} COP`;
          return (
            <div className="bg-white border border-[#E5E7EB] rounded-[12px] p-3 space-y-1">
              <div className="flex justify-between text-xs text-[#6B7280]">
                <span>Monto a convertir</span>
                <span>{fmtOrigen(netAmount)}</span>
              </div>
              <div className="flex justify-between text-xs text-[#6B7280]">
                <span>Comision FlowexPay ({feePct}%)</span>
                <span>{fmtOrigen(feeAmount)}</span>
              </div>
              <div className="flex justify-between text-sm font-bold text-[#0A2540] pt-1 border-t border-[#F3F4F6]">
                <span>Total a debitar</span>
                <span>{fmtOrigen(numericAmount)}</span>
              </div>
              <div className="flex justify-between text-sm font-bold text-[#059669]">
                <span>Recibes (aprox.)</span>
                <span>{fmtDestino(previewConverted)}</span>
              </div>
            </div>
          );
        })()}

        {exceedsBalance && (
          <p className="text-xs text-[#DC2626]">El monto excede tu saldo disponible.</p>
        )}
        {error && <p className="text-xs text-[#DC2626]">{error}</p>}

        <button
          onClick={handleConvert}
          disabled={!numericAmount || exceedsBalance || loading || !rate}
          className="w-full py-3 rounded-[12px] text-white font-bold disabled:opacity-50"
          style={{ background: 'linear-gradient(135deg, #0A2540, #1A3A6C)' }}
        >
          {loading ? 'Procesando...' : 'Convertir ahora'}
        </button>
      </div>
    </div>
  );
}
